/**
 * Sales Consultant home (modern design) — "my day, my pipeline, my customers".
 *
 * Real data only: the role snapshot scoped to this consultant (today's
 * counts, my funnel), my diary (today's schedule, trends), my leads from the
 * CRM (top leads, recent activity, models in my pipeline) and live stock
 * prices. The actionable My Day list (complete / book / next step) stays
 * below, unchanged.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCrm, stageLabel, stageColor, isClosedStage } from '../../lib/crm';
import { useCurrentUser } from '../../lib/currentUser';
import { useOrgName } from '../../lib/useOrgName';
import { useVehicleMap } from '../../lib/useVehicleMap';
import { dealValue, isDealStage, isOpenDeal } from '../../lib/deals';
import { formatInOrgTz } from '../../lib/datetime';
import { periodDays } from '../../components/dashboard/useRoleSnapshot';
import MyDay from '../MyDay';
import {
  Avatar, Hero, Panel, RankList, StatCard, TrendChart, Delta,
} from '../../components/dashboard/modern/ui';
import { Icon } from '../../components/dashboard/modern/icons';
import { fmtAud, fmtInt } from '../../components/dashboard/modern/format';
import {
  carRender, dayLabels, greeting, pipelineFunnel, showroomSlides, timeOf, useDiaryWindow, useSeriesPair, useTodaySchedule, withinDays,
} from '../../components/dashboard/modern/useModernData';
import FunnelViz from '../../components/dashboard/modern/FunnelViz';
import QuickActions from '../../components/dashboard/modern/QuickActions';

const TYPE_META = {
  TEST_DRIVE:      { label: 'Test drive',  icon: Icon.wheel,    tone: '#06b6d4' },
  HANDOVER:        { label: 'Handover',    icon: Icon.car,      tone: '#10b981' },
  FINANCE_MEETING: { label: 'Finance',     icon: Icon.coins,    tone: '#f59e0b' },
  FOLLOW_UP:       { label: 'Follow-up',   icon: Icon.phone,    tone: '#8b5cf6' },
  SIGNING:         { label: 'Signing',     icon: Icon.doc,      tone: '#ec4899' },
  GENERAL:         { label: 'Appointment', icon: Icon.calendar, tone: '#6366f1' },
};

export default function ConsultantHomeDashboard({ snapshot, period, periodLabel, contentRef }) {
  const navigate = useNavigate();
  const { user, consultantId } = useCurrentUser();
  const orgName = useOrgName();
  const { leads: crmLeads = [] } = useCrm();
  const { vehiclesById } = useVehicleMap({ size: 200 });

  const days = periodDays(period);
  const chartDays = Math.max(days, 14);
  const m = snapshot?.mine || {};
  const w = snapshot?.window || {};

  const leadSeries = useSeriesPair('leads', days, chartDays);       // scoped to me by the backend
  const diary = useDiaryWindow({ days, chartDays, consultantId });
  const today = useTodaySchedule(consultantId);

  const myLeads = useMemo(() => crmLeads.filter((l) => !l.simulated
    && (!consultantId || String(l.assignedConsultantId || '') === String(consultantId))), [crmLeads, consultantId]);
  const leadById = useMemo(() => new Map(crmLeads.map((l) => [l.id, l])), [crmLeads]);
  const vehicles = useMemo(() => [...(vehiclesById?.values?.() || [])], [vehiclesById]);

  const money = useMemo(() => {
    let pipeline = 0; let est = false; let open = 0;
    myLeads.forEach((l) => {
      if (!isDealStage(l.stage) || !isOpenDeal(l.stage)) return;
      const { value, estimated } = dealValue(l, l.vehicleOfInterestId ? vehiclesById.get(l.vehicleOfInterestId) : null);
      pipeline += value; open += 1; est ||= estimated;
    });
    return { pipeline, est, open };
  }, [myLeads, vehiclesById]);

  const openLeads = useMemo(() => myLeads.filter((l) => !isClosedStage(l.stage)), [myLeads]);
  const topLeads = useMemo(() => [...openLeads]
    .sort((a, b) => (b.score - a.score) || (new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0)))
    .slice(0, 6), [openLeads]);
  const recent = useMemo(() => [...myLeads]
    .filter((l) => l.lastActivityAt)
    .sort((a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt))
    .slice(0, 6), [myLeads]);
  const hotIdle = useMemo(() => openLeads.filter((l) => (l.score ?? 0) >= 80 && !withinDays(l.lastActivityAt, 1)).length, [openLeads]);
  const unactioned = useMemo(() => openLeads.filter((l) => l.stage === 'INBOUND').length, [openLeads]);

  const myModels = useMemo(() => {
    const counts = new Map();
    openLeads.forEach((l) => {
      const model = (l.vehicleModel || (l.vehicleOfInterestId && vehiclesById.get(l.vehicleOfInterestId)?.model) || '').trim();
      if (!model) return;
      const key = model.toUpperCase().split(/\s+/).slice(0, 2).join(' ');
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4).map(([key, value]) => {
      const first = key.split(' ')[0];
      const car = vehicles.find((v) => String(v.model || '').toUpperCase().startsWith(first));
      const make = car?.make || openLeads.find((l) => (l.vehicleModel || '').toUpperCase().startsWith(first))?.vehicleMake || '';
      return { key, label: `${make ? `${make} ` : ''}${titleCase(key)}`, value, photo: carRender(make, car?.model || key, car?.primaryPhotoUrl) };
    });
  }, [openLeads, vehicles, vehiclesById]);

  const funnel = useMemo(() => pipelineFunnel(myLeads), [myLeads]);

  // Next best actions — counted from my real data, highest stakes first.
  const actions = [
    m.tasksOverdue > 0 && { key: 'od', title: `Clear ${m.tasksOverdue} overdue task${m.tasksOverdue === 1 ? '' : 's'}`, sub: 'Customers are waiting on these', to: '/tasks' },
    hotIdle > 0 && { key: 'hot', title: `Call ${hotIdle} hot lead${hotIdle === 1 ? '' : 's'} gone quiet`, sub: 'High intent, no contact in 24h', to: '/leads?filter=hot' },
    unactioned > 0 && { key: 'new', title: `Make first contact with ${unactioned} new lead${unactioned === 1 ? '' : 's'}`, sub: 'Speed to lead wins the sale', to: '/leads?filter=unactioned' },
    m.leadsWithoutNextEvent > 0 && { key: 'nx', title: `Set a next step on ${m.leadsWithoutNextEvent} lead${m.leadsWithoutNextEvent === 1 ? '' : 's'}`, sub: 'Every open lead needs one', to: '/leads?filter=no-next-step' },
  ].filter(Boolean).slice(0, 4);

  const firstName = user?.firstName || (user?.displayName || user?.name || '').split(' ')[0] || '';
  const dateLine = formatInOrgTz(new Date(), { weekday: 'long', day: 'numeric', month: 'long' }, '');
  const heroSlides = useMemo(
    () => showroomSlides(myModels, vehicles, { badge: () => 'In your pipeline', unit: 'open lead' }),
    [myModels, vehicles],
  );

  return (
    <div className="app-content md-page" ref={contentRef}>
      <Hero
        eyebrow={`${dateLine}${orgName ? ` · ${orgName}` : ''}`}
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}!`}
        highlight="Let's keep the momentum going."
        sub={`You have ${fmtInt(m.appointmentsToday ?? today?.length ?? 0)} appointment${(m.appointmentsToday ?? 0) === 1 ? '' : 's'} and ${fmtInt(m.tasksDueToday ?? 0)} task${(m.tasksDueToday ?? 0) === 1 ? '' : 's'} today.`}
        chips={[
          { key: 'a', value: fmtInt(m.appointmentsToday ?? 0), label: 'appointments today', to: '/appointments' },
          { key: 't', value: fmtInt(m.tasksDueToday ?? 0), label: 'tasks due today', to: '/tasks' },
          ...(m.tasksOverdue > 0 ? [{ key: 'o', alert: true, value: fmtInt(m.tasksOverdue), label: 'overdue tasks', to: '/tasks' }] : []),
          { key: 'h', value: fmtInt(m.hotLeads ?? 0), label: 'hot leads', to: '/leads?filter=hot' },
        ]}
        slides={heroSlides}
      />

      <div className="md-stats">
        <StatCard index={0} label="My new leads" icon={Icon.users} tone="#6366f1" to="/leads"
          value={fmtInt(w.newLeads?.total ?? leadSeries.total)} delta={leadSeries.delta} spark={leadSeries.values} sub={`${periodLabel} · vs previous`} />
        <StatCard index={1} label="Appointments" icon={Icon.calendar} tone="#10b981" to="/appointments"
          value={fmtInt(diary.apptTotal ?? w.appointments?.booked)} delta={diary.apptDelta} spark={diary.appts} sub={periodLabel} />
        <StatCard index={2} label="Test drives" icon={Icon.wheel} tone="#06b6d4" to="/test-drives"
          value={fmtInt(diary.driveTotal ?? w.testDrives?.total)} delta={diary.driveDelta} spark={diary.drives} sub={periodLabel} />
        <StatCard index={3} label="Vehicles sold" icon={Icon.car} tone="#8b5cf6" to="/deals"
          value={fmtInt(w.sold?.total)} sub={periodLabel} />
        <StatCard index={4} label="My pipeline" icon={Icon.coins} tone="#f59e0b" to="/deals"
          value={`${money.est ? '~' : ''}${fmtAud(money.pipeline)}`} sub={`${fmtInt(money.open)} open deals`} />
        <StatCard index={5} label="Tasks today" icon={Icon.task} tone={m.tasksOverdue > 0 ? '#ef4444' : '#3b82f6'} to="/tasks"
          value={fmtInt(m.tasksDueToday)} sub={`${fmtInt(m.tasksOverdue ?? 0)} overdue`} />
      </div>

      <QuickActions actions={[
        { key: 'l', label: 'New lead', sub: 'Log an enquiry', icon: Icon.leadPlus, tone: '#3b82f6', to: '/leads?new=1' },
        { key: 'c', label: 'New customer', sub: 'Add a contact', icon: Icon.userPlus, tone: '#6366f1', to: '/customers?new=1' },
        { key: 'd', label: 'New deal', sub: 'Build a quote', icon: Icon.dealPlus, tone: '#10b981', to: '/quotes/new' },
        { key: 'a', label: 'Appointments', sub: 'Open the diary', icon: Icon.calendar, tone: '#f59e0b', to: '/appointments' },
        { key: 't', label: 'Test drives', sub: 'Start or book one', icon: Icon.wheel, tone: '#06b6d4', to: '/test-drives' },
        { key: 'k', label: 'My tasks', sub: `${fmtInt(m.tasksDueToday ?? 0)} due today`, icon: Icon.task, tone: '#8b5cf6', to: '/tasks' },
      ]} />

      <div className="md-row c3">
        <Panel title="My sales pipeline" sub="My leads, by the furthest stage reached." icon={Icon.funnel} link="/leads" linkLabel="Board">
          <FunnelViz funnel={funnel} title="My lead → delivered" />
        </Panel>

        <Panel title="Today's schedule" sub="Your appointments, in dealership time." icon={Icon.clock} tone="#10b981" link="/appointments" linkLabel="Calendar">
          {today == null ? <div className="md-empty">Loading…</div> : today.length ? (
            <div className="md-sched">
              {today.slice(0, 6).map((a) => {
                const meta = TYPE_META[a.type] || TYPE_META.GENERAL;
                const lead = a.leadId ? leadById.get(a.leadId) : null;
                const past = new Date(a.endsAt || a.startsAt) < new Date();
                return (
                  <Link key={a.id} to={a.leadId ? `/leads/${a.leadId}` : '/appointments'} className={`md-sched-row${past ? ' past' : ''}`}>
                    <span className="md-sched-time">{timeOf(a.startsAt)}</span>
                    <span className="md-icon" style={{ '--md-tone': meta.tone }}>{meta.icon}</span>
                    <span>
                      <div className="md-sched-title">{meta.label}{lead ? ` – ${lead.name}` : ''}</div>
                      <div className="md-sched-sub">{lead?.vehicleModel || lead?.vehicle || a.status.replace('_', ' ').toLowerCase()}</div>
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : <div className="md-empty">Nothing booked today — a good day to work your hot leads.</div>}
        </Panel>

        <Panel title="Next best actions" sub="What moves your numbers today." icon={Icon.sparkle} tone="#8b5cf6">
          {actions.length ? (
            <div className="md-actions">
              {actions.map((a, i) => (
                <Link key={a.key} to={a.to} className="md-action">
                  <span className="md-action-n">{i + 1}</span>
                  <span><div className="md-action-title">{a.title}</div><div className="md-action-sub">{a.sub}</div></span>
                  <span className="md-action-go">→</span>
                </Link>
              ))}
            </div>
          ) : <div className="md-empty">You're all caught up. 🎉</div>}
        </Panel>
      </div>

      <div className="md-row c2">
        <Panel title="My top leads" sub="Highest intent first." icon={Icon.flame} tone="#ef4444" link="/leads">
          {topLeads.length ? (
            <table className="md-table">
              <thead><tr><th>Customer</th><th>Vehicle</th><th>Stage</th><th>Heat</th></tr></thead>
              <tbody>
                {topLeads.map((l) => (
                  <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)}>
                    <td><span className="md-person"><Avatar name={l.name} size={26} />{l.name}</span></td>
                    <td>{l.vehicleModel || l.vehicle || '—'}</td>
                    <td><span className="md-pill" style={{ '--md-tone': stageColor(l.stage) }}>{stageLabel(l.stage)}</span></td>
                    <td className="md-heat" title={`Score ${l.score ?? 0}`}>{'🔥'.repeat(l.score >= 80 ? 3 : l.score >= 60 ? 2 : 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="md-empty">No open leads assigned to you yet.</div>}
        </Panel>

        <Panel title="My recent activity" sub="Latest movement on your leads." icon={Icon.clock} tone="#3b82f6">
          {recent.length ? (
            <div className="md-feed">
              {recent.map((l) => (
                <Link key={l.id} to={`/leads/${l.id}`} className="md-feed-row">
                  <span className="md-icon" style={{ '--md-tone': stageColor(l.stage) }}>{Icon.users}</span>
                  <span className="md-feed-title">{l.name} · {stageLabel(l.stage)}</span>
                  <span className="md-feed-when">{ago(l.lastActivityAt)}</span>
                </Link>
              ))}
            </div>
          ) : <div className="md-empty">No recent activity.</div>}
        </Panel>
      </div>

      <div className="md-row c2">
        <Panel title="My appointments & test drives" sub={`Daily, last ${chartDays} days.`} icon={Icon.calendar} tone="#10b981">
          <div className="md-trend">
            <div>
              <div className="md-trend-legend" style={{ marginBottom: 6 }}>
                <span><i style={{ background: '#6366f1' }} />Appointments</span>
                <span><i style={{ background: '#10b981', borderRadius: 2 }} />Test drives</span>
              </div>
              <TrendChart labels={dayLabels(chartDays)} series={[
                { label: 'Appointments', color: '#6366f1', values: diary.appts },
                { label: 'Test drives', color: '#10b981', values: diary.drives, type: 'bar' },
              ]} />
            </div>
            <div className="md-trend-side">
              <div><div className="md-big">{fmtInt(diary.apptTotal)}</div><div className="md-small">appointments · {periodLabel}</div><Delta value={diary.apptDelta} /></div>
              <div><div className="md-big">{fmtInt(diary.driveTotal)}</div><div className="md-small">test drives · {periodLabel}</div><Delta value={diary.driveDelta} /></div>
            </div>
          </div>
        </Panel>
        <Panel title="Models in my pipeline" sub="Open leads by vehicle." icon={Icon.car} tone="#8b5cf6" link="/inventory" linkLabel="Inventory">
          <RankList rows={myModels} />
        </Panel>
      </div>

      <div className="md-section"><h2>My Day</h2><span>Tasks and follow-ups — complete, book or set the next step.</span></div>
      <MyDay embedded />
    </div>
  );
}

function ago(iso) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins)) return '';
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'Yesterday' : `${d}d ago`;
}

function titleCase(s) {
  return String(s).toLowerCase().replace(/(^|[\s-])(\w)/g, (m, a, b) => a + b.toUpperCase());
}
